// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
/**
 * LA CONSIGNE PATIENT, RÉDUITE À CE QUI PRÉPARE (21/10/2026).
 *
 * SOCLE COMMUN depuis le 31/10/2026 : la même lecture sert au formulaire de
 * rendez-vous (site) et à la fiche du catalogue (manuel), qui l'affichent
 * donc à l'identique. Source : `patientPreparation` et `preanalytique` de la
 * fiche, réglés dans Nafis (Référentiel des analyses).
 *
 * `patientPreparation` est un texte de catalogue, une phrase par ligne :
 * « Une simple prise de sang / Pas besoin d’être à jeun / Résultat : le jour
 * même ». Affiché tel quel sous chaque analyse retenue, il faisait de chaque
 * ligne du formulaire un pavé — et noyait la seule chose que le patient doit
 * retenir AVANT de venir : être à jeun ou non, et la consigne particulière.
 *
 * On trie donc les lignes :
 *   · le JEÛNE devient une pastille (« À jeun 12 h », « Sans jeûne ») ;
 *   · le délai de résultat et « Une simple prise de sang » sortent — ils ne
 *     préparent rien ;
 *   · le reste (pièce d’identité, traitement, horaire…) est une CONSIGNE.
 *
 * ⚠️ Même tri côté moteur (`backend/src/utils/recap-rdv.ts`) pour l’e-mail de
 * récapitulatif : deux dépôts, pas d’import possible. Une règle changée ici se
 * change là.
 */

import { lireJeune, phraseJeune, dureeJeune, type CorrespondancesPatient } from './correspondancesPatient';

export type Jeune = 'requis' | 'preference' | 'non' | null;

export interface Preparation {
  jeune: Jeune;
  /** La ligne de jeûne telle qu’au catalogue (« À jeun : ne rien manger depuis 12 h… »). */
  jeuneTexte: string;
  /** « 12 h », « 8 à 12 h »… si la ligne le dit. */
  jeuneHeures: string;
  consignes: string[];
  /** Les consignes de RECUEIL propres à l'examen (case « recueil particulier »
   *  de la fiche, `preanalytique.recueilConsignes`), une par ligne. */
  recueil: string[];
}

/* Ce qui décrit le prélèvement sans rien demander au patient : « Un recueil
   d'urines » (31/10/2026) sort comme « Une simple prise de sang » — la
   consigne de recueil, elle, est sur les lignes suivantes. */
const SANS_PREPARATION = /^(une simple prise de sang|un recueil d.urines?|aucun régime particulier)\.?$/i;
const RESULTAT = /^résultat\s*:/i;
const PAS_A_JEUN = /pas besoin d.être à jeun/i;
const A_JEUN_PREF = /à jeun de préférence/i;
const A_JEUN = /à jeun/i;

/**
 * JEÛNE STRUCTURÉ D'ABORD (Ines, 31/10/2026) : quand la fiche porte
 * `preanalytique.jeune`, c'est lui qui décide, dit avec la phrase de la table
 * de correspondance patient. Les lignes de jeûne du texte sont alors ignorées
 * (elles ne deviennent jamais des consignes). Sans réglage structuré, on lit
 * le texte comme avant.
 */
export function lirePreparation(texte: string | null | undefined, preanalytique?: unknown, table?: CorrespondancesPatient): Preparation {
  const p = { ...lireTexte(texte), recueil: lireRecueil(preanalytique) };
  const j = lireJeune(preanalytique);
  if (!j) return p;
  /* Une phrase de jeûne qui disait AUSSI autre chose (« Le matin, à jeun ») :
     le champ structuré remplace le jeûne, le reste devient une consigne. */
  const reste = resteHorsJeune(p.jeuneTexte);
  return {
    ...p,
    consignes: reste ? [reste, ...p.consignes] : p.consignes,
    jeune: j.niveau === 'requis' ? 'requis' : j.niveau === 'recommande' ? 'preference' : 'non',
    jeuneTexte: phraseJeune(j, table),
    jeuneHeures: j.niveau === 'requis' ? dureeJeune(j).replace(/\s*h$/, '') : '',
  };
}

/** « Le matin, à jeun » → « Le matin » ; une phrase qui ne dit que le jeûne → ''. */
export function resteHorsJeune(ligne: string): string {
  if (!/matin|heure|\bh\b/i.test(ligne) || /ne rien manger|pas besoin/i.test(ligne)) return '';
  const r = ligne.replace(/[,;]?\s*(?:être\s+)?à jeun(?: de préférence)?/i, '').replace(/^[,;\s]+|[,;\s]+$/g, '').trim();
  return r ? r.charAt(0).toUpperCase() + r.slice(1) : '';
}

/** RECUEIL PARTICULIER (Ines, 31/10/2026) : une case dans la fiche Nafis ;
 *  cochée, les consignes de recueil écrites dessous sont montrées au patient. */
export function lireRecueil(preanalytique: unknown): string[] {
  if (!preanalytique || typeof preanalytique !== 'object') return [];
  const p = preanalytique as Record<string, unknown>;
  if (p.recueilParticulier !== true || typeof p.recueilConsignes !== 'string') return [];
  return p.recueilConsignes.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
}

function lireTexte(texte: string | null | undefined): Preparation {
  const p: Preparation = { jeune: null, jeuneTexte: '', jeuneHeures: '', consignes: [], recueil: [] };
  for (const brut of String(texte || '').split(/\r?\n/)) {
    const l = brut.trim();
    if (!l || RESULTAT.test(l) || SANS_PREPARATION.test(l)) continue;
    if (PAS_A_JEUN.test(l)) { if (!p.jeune) p.jeune = 'non'; continue; }
    if (A_JEUN_PREF.test(l)) { if (p.jeune !== 'requis') { p.jeune = 'preference'; p.jeuneTexte = l; } continue; }
    if (A_JEUN.test(l)) {
      p.jeune = 'requis'; p.jeuneTexte = l;
      p.jeuneHeures = /(\d+(?:\s*à\s*\d+)?)\s*h\b/.exec(l)?.[1].replace(/\s+/g, ' ') ?? '';
      continue;
    }
    p.consignes.push(l);
  }
  return p;
}

export interface Recapitulatif {
  /** La consigne de jeûne la plus stricte, et les analyses qui l’exigent. */
  jeune: { niveau: Exclude<Jeune, null>; texte: string; analyses: string[] } | null;
  /** Chaque consigne une seule fois, avec les analyses qui la portent. */
  consignes: { texte: string; analyses: string[] }[];
}

/** Les conditions de prélèvement de plusieurs analyses, regroupées : une
 *  consigne commune à trois analyses s’écrit une fois. */
export function recapPreparation(analyses: { label: string; preparation?: string | null; preanalytique?: unknown }[], table?: CorrespondancesPatient): Recapitulatif {
  const lues = analyses.map((a) => ({ label: a.label, p: lirePreparation(a.preparation, a.preanalytique, table) }));
  const requis = lues.filter((x) => x.p.jeune === 'requis');
  const pref = lues.filter((x) => x.p.jeune === 'preference');
  let jeune: Recapitulatif['jeune'] = null;
  if (requis.length) {
    // Le jeûne le plus strict couvre les autres : 12 h satisfait « 8 à 12 h »
    // — la borne haute d'abord, puis la basse.
    const duree = (x: typeof requis[number]) => {
      const n = (x.p.jeuneHeures.match(/\d+/g) ?? []).map(Number);
      return n.length ? Math.max(...n) * 100 + Math.min(...n) : 0;
    };
    const plusLong = [...requis].sort((a, b) => duree(b) - duree(a))[0] ?? requis[0];
    jeune = { niveau: 'requis', texte: plusLong.p.jeuneTexte, analyses: requis.map((x) => x.label) };
  } else if (pref.length) {
    jeune = { niveau: 'preference', texte: pref[0].p.jeuneTexte, analyses: pref.map((x) => x.label) };
  } else if (lues.some((x) => x.p.jeune === 'non')) {
    jeune = { niveau: 'non', texte: lues.find((x) => x.p.jeune === 'non' && x.p.jeuneTexte)?.p.jeuneTexte || 'Pas besoin d’être à jeun', analyses: [] };
  }
  const consignes: Recapitulatif['consignes'] = [];
  for (const x of lues) {
    for (const c of [...x.p.consignes, ...x.p.recueil]) {
      const deja = consignes.find((k) => k.texte.toLowerCase() === c.toLowerCase());
      if (deja) { if (!deja.analyses.includes(x.label)) deja.analyses.push(x.label); }
      else consignes.push({ texte: c, analyses: [x.label] });
    }
  }
  return { jeune, consignes };
}

/* ── L'HEURE DE PRÉLÈVEMENT (31/10/2026) ───────────────────────────────────
   `preanalytique.matin` (obligatoire | preference), `matinDe`, `matinA` —
   réglés dans Nafis ; même lecture que `backend/src/utils/horaire-prelevement.ts`. */
export interface HoraireMatin { niveau: 'obligatoire' | 'preference'; de: number | null; a: number | null }

const heureValide = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) && n >= 0 && n <= 24 ? n : null;
};

export function lireHoraire(preanalytique: unknown): HoraireMatin | null {
  if (!preanalytique || typeof preanalytique !== 'object') return null;
  const p = preanalytique as Record<string, unknown>;
  if (p.matin !== 'obligatoire' && p.matin !== 'preference') return null;
  const de = heureValide(p.matinDe); const a = heureValide(p.matinA);
  return { niveau: p.matin, de, a: a !== null && de !== null && a <= de ? null : a };
}

const heureCourte = (h: number) => { const m = Math.round((h % 1) * 60); return m ? `${Math.floor(h)} h ${String(m).padStart(2, '0')}` : `${Math.floor(h)} h`; };

/** « Le matin, entre 7 h et 9 h », « Le matin de préférence ». */
export function phraseHoraire(h: HoraireMatin): string {
  const plage = h.de !== null && h.a !== null ? `, entre ${heureCourte(h.de)} et ${heureCourte(h.a)}`
    : h.a !== null ? `, avant ${heureCourte(h.a)}` : h.de !== null ? `, à partir de ${heureCourte(h.de)}` : '';
  return h.niveau === 'obligatoire' ? `Prélèvement le matin${plage}` : `De préférence le matin${plage}`;
}
